import Vue from 'vue'
import uniq from 'lodash/uniq'

import Logger from '@/logger'

import AppError from '@/models/app-error'
import JWT from '@/models/jwt'

import { initialState } from './state'

import { MUTATIONS as IMPROVEMENTS } from './modules/improvements/mutations'
import { MUTATIONS as METRICS } from './modules/metrics/mutations'
import { MUTATIONS as MYHOME } from './modules/my-home/mutations'
import { MUTATIONS as ROUTES } from './modules/routes/mutations'
import { MUTATIONS as SERVICE_BASKET } from './modules/service-basket/mutations'

const MUTATIONS = {

    resetState: 'reset-state',
    resetNonContextState: 'reset-non-context-state',
    resetNonPropertyBaseState: 'reset-non-property-base-state',

    logAppError: 'log-app-error',
    clearAllErrors: 'mark-all-errors-displayed',
    clearOldErrors: 'mark-old-errors-displayed',

    setAuthContext: 'set-auth-context',
    setRequestToken: 'set-request-token',

    selectProperty: 'select-property',

    setProjectLastLoaded: 'set-project-last-loaded',
    clearProjectLastLoaded: 'clear-project-last-loaded',

    setPropertyCalculationInProgress: 'set-property-calculation-in-progress',
    setPropertyCalculationComplete: 'set-property-calculation-complete',
    setPropertySchemeInfo: 'set-property-scheme-info',

    setScheme: 'set-scheme',
    setUserLinkedProperties: 'set-user-linked-properties',

    setWindowWidth: 'set-window-width',
    setServiceDown: 'set-service-down',
    setPartnerReferralId: 'set-partner-referral-id',

    markVisitedRoute: 'mark-visited-route',
    clearVisitedRoute: 'clear-visited-route',
    clearAllVisitedRoutes: 'clear-all-visited-routes',

    setSearchedAddresses: 'set-searched-addresses',
    clearSearchedAddresses: 'clear-searched-addresses',

    IMPROVEMENTS,
    METRICS,
    MYHOME,
    ROUTES,
    SERVICE_BASKET,
}

export { MUTATIONS }

/**
 * Reset state to initial state, unless the key is in the exclude array.
 *
 * @param {VuexState} state
 * @param {string[]} exclude
 */
function resetState(state, exclude = []) {
    for (const item in initialState)
        if (!exclude.includes(item)) Vue.set(state, item, initialState[item])
}


export default {

    /**
     * Resets state.
     * @param {VuexState} state
     */
    [MUTATIONS.resetState](state) {
        resetState(state, [ 'scheme', 'requestToken', 'partnerReferralId' ])
        Logger.logObject(state, 'State reset', 'Store')
    },

    /**
     * Resets state except for current context.
     * @param {VuexState} state
     */
    [MUTATIONS.resetNonContextState](state) {
        resetState(state, [ 'authContext', 'scheme', 'requestToken', 'serviceDown', 'partnerReferralId' ])
        Logger.logObject(state, 'Non context state reset', 'Store')
    },

    /**
     * Resets state except for current context.
     * @param {VuexState} state
     */
    [MUTATIONS.resetNonPropertyBaseState](state) {
        resetState(state, [ 'authContext', 'scheme', 'property', 'partnerReferralId' ])
        Logger.logObject(state, 'Non property base state reset', 'Store')
    },

    /**
     * Logs an application error.
     * @param {VuexState} state
     * @param {AppError} error
     */
    [MUTATIONS.logAppError](state, error) {
        if (!(error instanceof AppError)) throw new TypeError('Vuex appErrors requires an AppError object model')
        state.appErrors.push(error)
        Logger.logObject(state, `State updated ${ MUTATIONS.logAppError }`, 'Errors')
    },

    /**
     * Marks all errors as displayed.
     * @param {VuexState} state
     */
    [MUTATIONS.clearAllErrors](state) {
        state.appErrors = []
        Logger.logObject(state, `State updated ${ MUTATIONS.clearAllErrors }`, 'Errors')
    },

    /**
     * Marks all errors except the latest as displayed.
     * @param {VuexState} state
     */
    [MUTATIONS.clearOldErrors](state) {
        state.appErrors = state.appErrors && state.appErrors.length ? [ state.appErrors[state.appErrors.length-1] ] : []
        Logger.logObject(state, `State updated ${ MUTATIONS.clearOldErrors }`, 'Errors')
    },

    /**
     * Clears the project load timestamp in state.
     * @param {VuexState} state
     */
    [MUTATIONS.clearProjectLastLoaded](state) {
        state.projectLastLoaded = null
        Logger.logObject(state, `State updated ${ MUTATIONS.setProjectLastLoaded }`, 'Project')
    },

    /**
     * Sets the property for the app to operate on.
     * @param {VuexState} state
     * @param {Property} property
     */
    [MUTATIONS.selectProperty](state, property) {
        if (property !== state.property) {
            state.property = property
            Logger.logObject(state, `State updated ${ MUTATIONS.selectProperty }`, 'Property')
        }
    },

    /**
     * Sets the authentication context for the app.
     *
     * @param {VuexState} state
     * @param {Context} authContext
     */
    [MUTATIONS.setAuthContext](state, authContext) {
        if (authContext !== state.authContext) {
            state.authContext = authContext
            Logger.logObject(state, `State updated ${ MUTATIONS.setAuthContext }`, 'Auth')
        }
    },

    /**
     * Sets the project load timestamp in state.
     * @param {VuexState} state
     */
    [MUTATIONS.setProjectLastLoaded](state) {
        state.projectLastLoaded = Date.now() / 1000
        Logger.logObject(state, `State updated ${ MUTATIONS.setProjectLastLoaded }`, 'Project')
    },

    /**
     * Sets the property calculation flag and updates the property data in state.
     * @param {VuexState} state
     */
    [MUTATIONS.setPropertyCalculationInProgress](state) {
        state.propertyCalculationInProgress = true

        if (state.property && state.property.calculated)
            state.property.calculated = false

        Logger.logObject(state, `State updated ${ MUTATIONS.setPropertyCalculationInProgress }`, 'Address')
    },

    /**
     * Sets the property calculation flag and updates the property data in state.
     * @param {VuexState} state
     */
    [MUTATIONS.setPropertyCalculationComplete](state) {
        state.propertyCalculationInProgress = false

        if (state.property && !state.property.calculated)
            state.property.calculated = true

        Logger.logObject(state, `State updated ${ MUTATIONS.setPropertyCalculationComplete }`, 'Address')
    },

    /**
     * Sets the scheme data for the property.
     * @param {VuexState} state
     * @param {LinkedSchemeData} schemeInfo
     */
    [MUTATIONS.setPropertySchemeInfo](state, schemeInfo) {
        if (schemeInfo !== state.propertyLinkedSchemes) {
            state.propertyLinkedSchemes = schemeInfo
            Logger.logObject(state, `State updated ${ MUTATIONS.setPropertySchemeInfo }`, 'Property')
            Logger.logObject(state, `State updated ${ MUTATIONS.setPropertySchemeInfo }`, 'Schemes')
        }
    },

    /**
     * Sets the scheme context for the app.
     * @param {VuexState} state
     * @param {Scheme} scheme
     */
    [MUTATIONS.setScheme](state, scheme) {
        if (scheme !== state.scheme) {
            state.scheme = scheme
            Logger.logObject(state, `State updated ${ MUTATIONS.setScheme }`, 'Schemes')
        }
    },

    /**
     * Sets the array of linked properties for a user.
     * @param {VuexState} state
     * @param {Array<Property>} properties
     */
    [MUTATIONS.setUserLinkedProperties](state, properties) {
        if (properties !== state.userLinkedProperties) {
            state.userLinkedProperties = properties
            Logger.logObject(state, `State updated ${ MUTATIONS.setUserLinkedProperties }`, 'Address')
        }
    },

    /**
     * Sets the window width in store.
     * @param {VuexState} state
     */
    [MUTATIONS.setWindowWidth](state) {
        state.windowWidth = window && window.innerWidth
    },

    /**
     * @param {VuexState} state
     * @param {string} token
     */
    [MUTATIONS.setRequestToken](state, token) {
        state.requestToken = JWT.ensure(token)

        Logger.logObject({ ...state }, `State updated ${ MUTATIONS.setRequestToken }`, 'Token')
    },

    /**
     * Sets the service up or down state.
     * @param {VuexState} state
     * @param {boolean} serviceDown
     */
    [MUTATIONS.setServiceDown](state, serviceDown) {
        state.serviceDown = serviceDown

        Logger.logObject({ ...state }, `State updated ${ MUTATIONS.setServiceDown }`, 'ServiceDown')
    },

    /**
     * Sets the reference number for a partner referral.
     * @param {VuexState} state
     * @param {?string} partnerReferralId
     */
    [MUTATIONS.setPartnerReferralId](state, partnerReferralId) {
        state.partnerReferralId = partnerReferralId

        Logger.logObject({ ...state }, `State updated ${ MUTATIONS.setPartnerReferralId }`, 'PartnerReference')
    },

    /**
     * Mark the supplied route as visited.
     * @param {VuexState} state
     * @param {string} routeName
     */
    [MUTATIONS.markVisitedRoute](state, routeName) {
        state.visitedRoutes = uniq([ ...state.visitedRoutes, routeName ])

        Logger.logObject(state.visitedRoutes, `State updated ${ MUTATIONS.markVisitedRoute }`, 'VisitedRoute')
    },

    /**
     * Clear the supplied route from the visited list.
     * @param {VuexState} state
     * @param {string} routeName
     */
    [MUTATIONS.clearVisitedRoute](state, routeName) {
        state.visitedRoutes = state.visitedRoutes.filter(r => r !== routeName)

        Logger.logObject(state.visitedRoutes, `State updated ${ MUTATIONS.clearVisitedRoute }`, 'VisitedRoute')
    },

    /**
     * Clear all visited routes.
     * @param {VuexState} state
     */
    [MUTATIONS.clearAllVisitedRoutes](state) {
        state.visitedRoutes = []

        Logger.logObject(state.visitedRoutes, `State updated ${ MUTATIONS.clearAllVisitedRoutes }`, 'VisitedRoute')
    },

    /**
     * Store list of addresses by postcode.
     * @param {VuexState} state
     * @param {string} postcode
     * @param {Address[]} addressList
     */
    [MUTATIONS.setSearchedAddresses](state, { postcode, addressList }) {
        state.searchedAddresses[postcode] = addressList
        Logger.logObject({ ...state.searchedAddresses }, `State updated ${ MUTATIONS.clearAllVisitedRoutes }`, 'AddressSearch')
    },

    /**
     * Clear searched address list.
     * @param {VuexState} state
     */
    [MUTATIONS.clearSearchedAddresses](state) {
        state.searchedAddresses = {}
        Logger.logObject({ ...state.searchedAddresses }, `State updated ${ MUTATIONS.clearAllVisitedRoutes }`, 'AddressSearch')
    },
}
